import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, ShoppingCart, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { guardedSupabaseQuery } from '@/utils/supabaseGuard';
import { productionLogger } from '@/utils/production-logger';

export function SalesStatsWidget() {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    ordersToday: 0,
    conversionRate: 0,
    avgOrderValue: 0,
    loading: true,
    error: null as string | null
  });

  useEffect(() => {
    const fetchSalesStats = async () => {
      try {
        // Enhanced Security: Check rate limiting before proceeding
        const { data: rateLimitCheck, error: rateLimitError } = await supabase
          .rpc('check_admin_dashboard_rate_limit');
          
        if (rateLimitError) {
          throw new Error(`Rate limit check failed: ${rateLimitError.message}`);
        }
        
        if (!rateLimitCheck) {
          throw new Error('Rate limit exceeded for sales stats widget access');
        }

        // Enhanced Security: Log widget access for audit trail
        const { error: logError } = await supabase
          .rpc('log_dashboard_widget_access', {
            p_widget_type: 'sales_stats',
            p_data_points: 1
          });
          
        if (logError) {
          productionLogger.warn('Failed to log sales widget access:', logError);
          // Continue despite logging failure - don't block widget functionality
        }

        // Get today's date range
        const today = new Date();
        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const endOfDay = new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000);

        const [ordersResult, revenueResult] = await Promise.all([
          guardedSupabaseQuery(
            async () => supabase
              .from('orders')
              .select('id, total_amount')
              .gte('created_at', startOfDay.toISOString())
              .lt('created_at', endOfDay.toISOString()),
            { fallbackData: [], errorMessage: 'Unable to load orders data' }
          ),
          guardedSupabaseQuery(
            async () => supabase
              .from('orders')
              .select('total_amount')
              .eq('status', 'completed'),
            { fallbackData: [], errorMessage: 'Unable to load revenue data' }
          )
        ]);

        const todaysOrders = ordersResult.data || [];
        const allCompletedOrders = revenueResult.data || [];

        const totalRevenue = allCompletedOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0);
        const avgOrderValue = allCompletedOrders.length > 0 ? totalRevenue / allCompletedOrders.length : 0;

        // Mock conversion rate calculation (would need visitor data for real calculation)
        const conversionRate = todaysOrders.length > 0 ? Math.min(todaysOrders.length * 2.5, 100) : 0;

        setStats({
          totalRevenue,
          ordersToday: todaysOrders.length,
          conversionRate,
          avgOrderValue,
          loading: false,
          error: null
        });
      } catch (err) {
        productionLogger.error('Failed to fetch sales stats:', err);
        setStats(prev => ({
          ...prev,
          loading: false,
          error: err instanceof Error && err.message.includes('Rate limit exceeded') 
            ? 'Rate limit exceeded - please wait before refreshing'
            : err instanceof Error && err.message.includes('Rate limit check failed')
            ? 'Security check failed - contact administrator'
            : 'Unable to load sales data'
        }));
      }
    };

    fetchSalesStats();
  }, []);

  if (stats.loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Sales Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded w-16"></div>
            <div className="h-4 bg-muted rounded w-24"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (stats.error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-destructive" />
            Sales Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive text-sm">{stats.error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-primary" />
          Sales Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Revenue</span>
            <span className="text-sm font-semibold text-primary">
              ${stats.totalRevenue.toLocaleString()}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <ShoppingCart className="w-3 h-3" />
              Today
            </span>
            <span className="text-sm font-medium">
              {stats.ordersToday}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              Conv Rate
            </span>
            <span className="text-sm font-medium">
              {stats.conversionRate.toFixed(1)}%
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="w-3 h-3" />
              AOV
            </span>
            <span className="text-sm font-medium">
              ${stats.avgOrderValue.toFixed(0)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}